import { useContext } from "react";

import { InlineOptionsListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { EncounterDateContext } from "@/Flowsheets/Contexts/EncounterDateContext";
import { BooleanReviewedCareElement } from "@/Flowsheets/Models/CareElements/BooleanReviewedCareElement";
import { CareElementDateReviewedRow } from "./CareElementDateReviewedRow";

interface ICareElementBooleanNumberDateReviewedProps {
    isEditable: boolean;
    label: string;
    side?: "row" | "column";
    element: BooleanReviewedCareElement;
    onChange: (element: BooleanReviewedCareElement) => void;
}

/**
 * Yes/No control for the date reviewed care element.
 * @param props - The properties for the component.
 * @property {string} props.id - The unique identifier for the care element.
 * @property {BooleanReviewedCareElement} props.element - The care element to be displayed.
 * @property {function} props.onChange - Callback function to handle changes to the care element.
 * @property {boolean} props.isEditable - Indicates if the care element is editable.
 * @property {string} props.label - The label for the care element.
 * @property {string} props.side - The side on which the care element is displayed (row or column).
 * @returns JSX.Element
 */
export function CareElementYesNoControl(props: Readonly<ICareElementBooleanNumberDateReviewedProps>): JSX.Element {
    const { element, onChange, isEditable, side = "row", label } = props;

    const { encounterDateTimeWithPrecision } = useContext(EncounterDateContext);

    return (
        <CareElementDateReviewedRow
            element={element}
            onChange={onChange}
            isEditable={isEditable}
            label={label}
            side={side}
            setFalseAsValid
        >
            <InlineOptionsListControl.YesOrNo
                id={`care-element-yes-no-${element?.Name}`}
                value={element.Value}
                disabled={!isEditable}
                onChange={option => {
                    let yesOrNoValue: boolean | null = null;
                    if (element?.Value !== option.Key) {
                        yesOrNoValue = option.Key;
                    }

                    const isValid = yesOrNoValue !== null;

                    const shouldAutoFillDate = isValid && !element.DateReviewedWithPrecision?.value;

                    let dateToUse = null;

                    if (isValid) {
                        dateToUse = shouldAutoFillDate
                            ? encounterDateTimeWithPrecision
                            : element.DateReviewedWithPrecision;
                    }

                    onChange({ ...element, Value: yesOrNoValue, DateReviewedWithPrecision: dateToUse });
                }}
            />
        </CareElementDateReviewedRow>
    );
}
